import React from "react";
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonIcon,
  IonBadge,
  IonList,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  RefresherEventDetail,
  IonButton,
  IonText,
} from "@ionic/react";
import {
  timeOutline,
  calendarOutline,
  checkmarkCircleOutline,
  ellipseOutline,
  arrowForwardOutline,
} from "ionicons/icons";
import { useDashboard } from "../hooks/useDashboard";
import { useAuth } from "@/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { formatNumber, getDeviceLocale } from "@/utils";
import "./Home.css";
import DayPlanningCard from "@components/DayPlanningCard.tsx";
import ClientAddressLines from "@components/ClientAddressLines.tsx";

const Home: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: dashboard, isLoading } = useDashboard();

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    event.detail.complete();
  };

  const formatHours = (hours: number) => {
    const formatted = formatNumber(hours, 1);
    const locale = getDeviceLocale();
    const decimalSeparator = new Intl.NumberFormat(locale).format(1.1)[1];
    return formatted.replace(`${decimalSeparator}0`, "");
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Goedemorgen";
    if (hour < 18) return "Goedemiddag";
    return "Goedenavond";
  };

  return (
    <IonPage className="home-page">
      <IonContent className="ion-padding">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className="home-container">
          {/* Welcome Section */}
          <div className="welcome-section">
            <h2>
              {getGreeting()}, {user?.name?.split(" ")[0]}!
            </h2>
            <p>
              {new Date().toLocaleDateString("nl-NL", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {/* Stats Grid */}
          <IonGrid className="stats-grid">
            <IonRow className="mb-2">
              <IonCol size="6">
                <IonCard className="stat-card">
                  <IonCardContent>
                    <div className="tw:inline-block stat-icon-container tw:mb-4">
                      <IonIcon
                        icon={timeOutline}
                        color="white"
                        size="large"
                        className="stat-icon"
                      />
                    </div>

                    <IonLabel color="medium" className="tw:uppercase">
                      <div>Vandaag</div>
                    </IonLabel>

                    {isLoading ? (
                      <IonSkeletonText animated style={{ width: "60%" }} />
                    ) : (
                      <>
                        <div className="stat-value ">
                          <IonText
                            className="tw:font-bold tw:text-2xl"
                            color="dark"
                          >
                            {formatHours(dashboard?.stats.hours_today || 0)}
                          </IonText>
                          <IonText className="tw:font-bold" color="medium">
                            u
                          </IonText>
                        </div>
                      </>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>

              <IonCol size="6">
                <IonCard className="stat-card">
                  <IonCardContent>
                    <div className="tw:inline-block stat-icon-container tw:mb-4">
                      <IonIcon
                        icon={calendarOutline}
                        size="large"
                        color="white"
                        className="stat-icon"
                      />
                    </div>

                    <IonLabel color="medium" className="tw:uppercase">
                      <div>Deze week</div>
                    </IonLabel>

                    {isLoading ? (
                      <IonSkeletonText animated style={{ width: "60%" }} />
                    ) : (
                      <>
                        <div className="stat-value ">
                          <IonText
                            className="tw:font-bold tw:text-2xl"
                            color="dark"
                          >
                            {formatHours(dashboard?.stats.hours_week || 0)}
                          </IonText>
                          <IonText className="tw:font-bold" color="medium">
                            u
                          </IonText>
                        </div>
                      </>
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>

          <br />
          <div className="tw:flex tw:justify-between tw:items-center">
            <h2>Planning vandaag</h2>
            <IonButton fill="clear" size="small" routerLink="/tabs/planning">
              Bekijk alles
              <IonIcon icon={arrowForwardOutline} slot="end" />
            </IonButton>
          </div>
          <IonCard>
            <IonCardContent>
              {isLoading ? (
                <>
                  <IonSkeletonText
                    animated
                    style={{ height: "60px", marginBottom: "10px" }}
                  />
                  <IonSkeletonText animated style={{ height: "60px" }} />
                </>
              ) : dashboard?.todays_planning?.length === 0 ? (
                <div className="empty-state">
                  <IonIcon icon={calendarOutline} />
                  <p>Geen planning voor vandaag</p>
                </div>
              ) : (
                <IonList lines="full">
                  {dashboard?.todays_planning?.map((planning) => (
                    <DayPlanningCard key={planning.id} item={planning} />
                  ))}
                </IonList>
              )}
              <IonButton
                expand="full"
                className="tw:mt-4"
                routerLink="/tabs/planning"
              >
                <IonIcon icon={calendarOutline} slot="start" />
                <IonLabel>Naar planning</IonLabel>
              </IonButton>
            </IonCardContent>
          </IonCard>

          <div className="tw:flex tw:justify-between tw:items-center">
            <IonText>
              <h2>Recente uren</h2>
            </IonText>
            <IonButton fill="clear" size="small" routerLink="/tabs/timesheets">
              Bekijk alles
              <IonIcon icon={arrowForwardOutline} slot="end" />
            </IonButton>
          </div>
          <IonCard>
            <IonCardHeader>
              <IonCardTitle></IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {isLoading ? (
                <>
                  <IonSkeletonText
                    animated
                    style={{ height: "50px", marginBottom: "10px" }}
                  />
                  <IonSkeletonText animated style={{ height: "50px" }} />
                </>
              ) : dashboard?.recent_timesheets?.length === 0 ? (
                <div className="empty-state">
                  <IonIcon icon={timeOutline} />
                  <p>Nog geen uren geregistreerd</p>
                </div>
              ) : (
                <IonList lines="full" className="rounded-md">
                  {dashboard?.recent_timesheets
                    ?.slice(0, 5)
                    .map((timesheet) => (
                      <IonItem key={timesheet.id} className="timesheet-item">
                        <IonIcon
                          icon={
                            timesheet.project?.status === "completed"
                              ? checkmarkCircleOutline
                              : ellipseOutline
                          }
                          slot="start"
                          color={
                            timesheet.project?.status === "completed"
                              ? "success"
                              : "primary"
                          }
                        />
                        <IonLabel>
                          {timesheet.project && (
                            <>
                              {timesheet.project.client && (
                                <ClientAddressLines
                                  client={timesheet.project.client}
                                />
                              )}
                              <h3>{timesheet.project.name}</h3>
                            </>
                          )}
                          <p>
                            {new Date(timesheet.date).toLocaleDateString(
                              "nl-NL",
                              {
                                weekday: "short",
                                day: "numeric",
                                month: "short",
                              }
                            )}
                          </p>
                        </IonLabel>
                        <IonBadge
                          color="primary"
                          className="px-2 py-1 font-size-12"
                        >
                          {formatHours(timesheet.hours)} uur
                        </IonBadge>
                      </IonItem>
                    ))}
                </IonList>
              )}
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Home;
